"use client";

import { useState } from "react";
import { Slider } from "@velpha/ui/components/slider";
import { COLOR, IllustrationCard } from "./_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

// ── Strings ────────────────────────────────────────────────────────

type ProductKey =
  | "savings"
  | "timeDeposit"
  | "govBond"
  | "corpBond"
  | "fund"
  | "etf"
  | "reit"
  | "gold"
  | "equity"
  | "crypto"
  | "deposit"
  | "bond";

const STRINGS: Record<
  Locale,
  {
    // ゾーンバッジの幅は文字数から出しているので、字幅も locale ごとに持つ。
    charWidth: number;
    product: Record<ProductKey, string>;
    zone: { short: string; mid: string; long: string };
    horizon: { short: string; mid: string; long: string };
    tier: { safe: string; lowVol: string; midVol: string; highVol: string };
    axisHorizon: string;
    axisRisk: string;
    mapTitle: string;
    mapDescription: string;
    flowTitle: string;
    flowDescription: string;
    commonColumn: string;
    recommendedColumn: string;
    common: { label: string; sub: string }[];
    recommended: { roles: string; products: string; weights: string; sustain: string };
    riskLow: string;
    riskHigh: string;
    defensive: string;
    growth: string;
    expectedReturn: string;
    risk: string;
    money: (value: number) => string;
    footnote: string;
  }
> = {
  ja: {
    charWidth: 10.5,
    product: {
      savings: "普通預金",
      timeDeposit: "定期預金",
      govBond: "国債",
      corpBond: "社債",
      fund: "投資信託",
      etf: "ETF",
      reit: "REIT",
      gold: "金",
      equity: "株式",
      crypto: "仮想通貨",
      deposit: "預金",
      bond: "債券",
    },
    zone: { short: "生活防衛資金", mid: "近いうちに使うお金", long: "長期で育てるお金" },
    horizon: { short: "1年未満", mid: "1〜5年", long: "10年以上" },
    tier: { safe: "元本保証", lowVol: "変動小", midVol: "変動中", highVol: "変動大" },
    axisHorizon: "運用期間 →",
    axisRisk: "リスク ↑",
    mapTitle: "運用期間とリスクで見る金融商品マップ",
    mapDescription: "左下ほど安全で短期向き、右上ほどリスクが高く長期向き。投資信託は商品によって幅がある",
    flowTitle: "商品選びの順番を比べる",
    flowDescription: "先に目的と時間軸を決めると、商品を見るときの判断軸ができます",
    commonColumn: "よくある順番",
    recommendedColumn: "目的から考える順番",
    common: [
      { label: "人気の商品を探す", sub: "SNSやランキングで気になる商品を見る" },
      { label: "とりあえず買ってみる", sub: "目的・期限・金額はまだ曖昧なまま" },
      { label: "値動きが気になる", sub: "下がったときに、どうすればいいか分からない" },
      { label: "売るか続けるかで迷う", sub: "判断する軸がないまま、日々の値動きに一喜一憂する" },
    ],
    recommended: {
      roles: "お金の役割分担と目的を明確にする",
      products: "金融商品の特徴を理解する",
      weights: "保有割合を考える",
      sustain: "納得して続けられる",
    },
    riskLow: "リスク低",
    riskHigh: "リスク高",
    defensive: "守り重視",
    growth: "成長重視",
    expectedReturn: "期待リターン",
    risk: "リスク",
    money: (value) => `${value.toLocaleString("ja-JP")}円`,
    footnote: "※ 100万円運用した場合の年間目安。実際の商品や相関によって変わります",
  },
  en: {
    charWidth: 6.2,
    product: {
      savings: "Savings",
      timeDeposit: "Time deposit",
      govBond: "Gov. bonds",
      corpBond: "Corp. bonds",
      fund: "Mutual funds",
      etf: "ETF",
      reit: "REITs",
      gold: "Gold",
      equity: "Equities",
      crypto: "Crypto",
      deposit: "Deposits",
      bond: "Bonds",
    },
    zone: { short: "Emergency fund", mid: "Money you need soon", long: "Money you grow long term" },
    horizon: { short: "Under 1 year", mid: "1–5 years", long: "10+ years" },
    tier: { safe: "Principal safe", lowVol: "Small swings", midVol: "Medium swings", highVol: "Large swings" },
    axisHorizon: "Time horizon →",
    axisRisk: "Risk ↑",
    mapTitle: "A map of products by time horizon and risk",
    mapDescription:
      "Lower left is safer and suits short horizons; upper right carries more risk and suits long horizons. Mutual funds span a wide range depending on what they hold",
    flowTitle: "Compare the order in which you pick products",
    flowDescription: "Deciding your purpose and horizon first gives you a yardstick for judging products",
    commonColumn: "The common order",
    recommendedColumn: "Starting from your purpose",
    common: [
      { label: "Look for popular products", sub: "Browse what you see on social media and ranking lists" },
      { label: "Just buy something", sub: "Purpose, deadline, and amount are still vague" },
      { label: "The price moves start to worry you", sub: "When it falls, you do not know what to do" },
      { label: "You waver between selling and holding", sub: "With no yardstick, each day's move swings your mood" },
    ],
    recommended: {
      roles: "Decide what each pot of money is for",
      products: "Understand what each product does",
      weights: "Decide how much of each to hold",
      sustain: "You can keep going with confidence",
    },
    riskLow: "Lower risk",
    riskHigh: "Higher risk",
    defensive: "Defensive",
    growth: "Growth",
    expectedReturn: "Expected return",
    risk: "Risk",
    money: (value) => `¥${value.toLocaleString("en-US")}`,
    footnote: "Annual guide on ¥1,000,000. Actual products and correlations will change these figures.",
  },
};

// ── Bubble chart: X = time horizon, Y = risk ───────────────────────

type Bubble = {
  key: ProductKey;
  x: number;
  y: number;
  r: number;
  color: string;
};

const CHART = { w: 640, h: 450, left: 60, right: 10, top: 20, bottom: 10 } as const;
const plotL = CHART.left;
const plotR = CHART.w - CHART.right;
const plotT = CHART.top;
const plotB = CHART.h - CHART.bottom;

// Zone boundaries
const pw = plotR - plotL;
const zoneShort = { x0: plotL, x1: plotL + pw * 0.18 };
const zoneMid = { x0: plotL + pw * 0.21, x1: plotL + pw * 0.52 };
const zoneLong = { x0: plotL + pw * 0.55, x1: plotR };

// Y-axis tiers
const ph = plotB - plotT;
const tierSafe = plotB - ph * 0.1;
const tierLowVol = plotB - ph * 0.32;
const tierMidVol = plotB - ph * 0.56;
const tierHighVol = plotB - ph * 0.82;

// Blended colors for zone-spanning products
const AMBER_SKY = "#7c8a2e";
const TEAL = "#0d9488";

const BUBBLES: Bubble[] = [
  // 1年未満 zone
  { key: "savings", x: (zoneShort.x0 + zoneShort.x1) / 2, y: tierSafe, r: 38, color: COLOR.amber },
  // Short/Mid boundary
  { key: "timeDeposit", x: (zoneShort.x1 + zoneMid.x0) / 2, y: tierSafe + 4, r: 34, color: AMBER_SKY },
  // 1〜5年 zone
  { key: "govBond", x: zoneMid.x0 + (zoneMid.x1 - zoneMid.x0) * 0.3, y: tierLowVol + 10, r: 32, color: COLOR.sky },
  { key: "corpBond", x: zoneMid.x0 + (zoneMid.x1 - zoneMid.x0) * 0.7, y: tierLowVol - 24, r: 32, color: COLOR.sky },
  // Mid/Long boundary
  { key: "fund", x: (zoneMid.x1 + zoneLong.x0) / 2 - 12, y: tierMidVol + 14, r: 40, color: TEAL },
  { key: "etf", x: (zoneMid.x1 + zoneLong.x0) / 2 + 48, y: tierMidVol - 30, r: 30, color: TEAL },
  // 10年以上 zone
  {
    key: "reit",
    x: zoneLong.x0 + (zoneLong.x1 - zoneLong.x0) * 0.14,
    y: tierMidVol + 22,
    r: 32,
    color: COLOR.emerald,
  },
  { key: "gold", x: zoneLong.x0 + (zoneLong.x1 - zoneLong.x0) * 0.34, y: tierMidVol - 16, r: 30, color: COLOR.emerald },
  {
    key: "equity",
    x: zoneLong.x0 + (zoneLong.x1 - zoneLong.x0) * 0.58,
    y: tierHighVol + 12,
    r: 38,
    color: COLOR.emerald,
  },
  {
    key: "crypto",
    x: zoneLong.x0 + (zoneLong.x1 - zoneLong.x0) * 0.84,
    y: tierHighVol - 22,
    r: 32,
    color: COLOR.emerald,
  },
];

// Y-axis tier labels
const TIER_LABELS = [
  { y: tierSafe, key: "safe" },
  { y: tierLowVol, key: "lowVol" },
  { y: tierMidVol, key: "midVol" },
  { y: tierHighVol, key: "highVol" },
] as const;

function AssetBubbleChart() {
  const t = STRINGS[useDocLocale()];
  return (
    <svg viewBox={`0 0 ${CHART.w} ${CHART.h}`} className="min-h-[320px] w-full sm:min-h-[420px]">
      {/* ── Zone backgrounds ── */}
      <rect
        x={zoneShort.x0}
        y={plotT}
        width={zoneShort.x1 - zoneShort.x0}
        height={plotB - plotT}
        rx={8}
        fill={COLOR.amber}
        opacity={0.18}
      />
      <rect
        x={zoneMid.x0}
        y={plotT}
        width={zoneMid.x1 - zoneMid.x0}
        height={plotB - plotT}
        rx={8}
        fill={COLOR.sky}
        opacity={0.18}
      />
      <rect
        x={zoneLong.x0}
        y={plotT}
        width={zoneLong.x1 - zoneLong.x0}
        height={plotB - plotT}
        rx={8}
        fill={COLOR.emerald}
        opacity={0.18}
      />

      {/* ── Zone badge labels (top) ── */}
      {[
        { cx: (zoneShort.x0 + zoneShort.x1) / 2, label: t.zone.short, color: COLOR.amber },
        { cx: (zoneMid.x0 + zoneMid.x1) / 2, label: t.zone.mid, color: COLOR.sky },
        { cx: (zoneLong.x0 + zoneLong.x1) / 2, label: t.zone.long, color: COLOR.emerald },
      ].map((z) => {
        const tw = z.label.length * t.charWidth + 20;
        return (
          <g key={z.label}>
            <rect
              x={z.cx - tw / 2}
              y={plotT - 4}
              width={tw}
              height={26}
              rx={13}
              fill={z.color}
              opacity={0.28}
              stroke={z.color}
              strokeWidth={1}
              strokeOpacity={0.6}
            />
            <text
              x={z.cx}
              y={plotT + 12}
              textAnchor="middle"
              fontSize="10"
              fill={z.color}
              fontWeight="600"
              opacity={0.9}
            >
              {z.label}
            </text>
          </g>
        );
      })}

      {/* ── Grid lines at tier positions ── */}
      {TIER_LABELS.map((tier) => (
        <line
          key={tier.key}
          x1={plotL}
          y1={tier.y}
          x2={plotR}
          y2={tier.y}
          stroke="currentColor"
          strokeDasharray="4 7"
          opacity={0.2}
        />
      ))}

      {/* ── Axes ── */}
      <line x1={plotL} y1={plotB} x2={plotR} y2={plotB} stroke="currentColor" strokeWidth={1.2} opacity={0.35} />
      <line x1={plotL} y1={plotT} x2={plotL} y2={plotB} stroke="currentColor" strokeWidth={1.2} opacity={0.35} />

      {/* X axis labels */}
      <text
        x={(zoneShort.x0 + zoneShort.x1) / 2}
        y={plotB + 22}
        textAnchor="middle"
        fontSize="12"
        fill="currentColor"
        opacity={0.65}
      >
        {t.horizon.short}
      </text>
      <text
        x={(zoneMid.x0 + zoneMid.x1) / 2}
        y={plotB + 22}
        textAnchor="middle"
        fontSize="12"
        fill="currentColor"
        opacity={0.65}
      >
        {t.horizon.mid}
      </text>
      <text
        x={(zoneLong.x0 + zoneLong.x1) / 2}
        y={plotB + 22}
        textAnchor="middle"
        fontSize="12"
        fill="currentColor"
        opacity={0.65}
      >
        {t.horizon.long}
      </text>
      <text x={plotR} y={plotB + 42} textAnchor="end" fontSize="10" fill="currentColor" opacity={0.45}>
        {t.axisHorizon}
      </text>

      {/* Y axis tier labels */}
      {TIER_LABELS.map((tier) => (
        <text
          key={tier.key}
          x={plotL - 10}
          y={tier.y + 4.5}
          textAnchor="end"
          fontSize="11"
          fill="currentColor"
          opacity={0.55}
        >
          {t.tier[tier.key]}
        </text>
      ))}
      <text x={plotL - 10} y={plotT + 12} textAnchor="end" fontSize="10" fill="currentColor" opacity={0.45}>
        {t.axisRisk}
      </text>

      {/* ── Bubbles ── */}
      {BUBBLES.map((b) => (
        <g key={b.key}>
          <circle
            cx={b.x}
            cy={b.y}
            r={b.r}
            fill={b.color}
            opacity={0.4}
            stroke={b.color}
            strokeWidth={2}
            strokeOpacity={0.7}
          />
          <text
            x={b.x}
            y={b.y + 1}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="13"
            fill="currentColor"
            fontWeight="500"
            opacity={0.85}
          >
            {t.product[b.key]}
          </text>
        </g>
      ))}
    </svg>
  );
}

// ── Exports ────────────────────────────────────────────────────────

export function DocMoneyRoleMap() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard title={t.mapTitle} description={t.mapDescription}>
      <AssetBubbleChart />
    </IllustrationCard>
  );
}

// ── Flowchart ────────────────────────────────────────────────────

type FlowStep = { label: string; sub?: string; content?: React.ReactNode };

function StepArrow({ color }: { color: string }) {
  return (
    <div className="flex h-4 w-full items-center justify-center" aria-hidden="true">
      <svg width="12" height="14" viewBox="0 0 12 14" className="shrink-0">
        <path d="M 6 0 L 6 9 M 2.5 7 L 6 12 L 9.5 7" fill="none" stroke={color} strokeWidth={1.2} opacity={0.4} />
      </svg>
    </div>
  );
}

function StepBox({ step, color, stepNum }: { step: FlowStep; color: string; stepNum?: number }) {
  return (
    <div
      className="flex w-full items-start gap-2 border px-2.5 py-2 text-left"
      style={{
        borderColor: `${color}40`,
        backgroundColor: `${color}06`,
        borderRadius: 6,
      }}
    >
      {stepNum != null && (
        <span
          className="flex size-5 shrink-0 items-center justify-center rounded text-[10px] font-bold text-white"
          style={{ backgroundColor: color, opacity: 0.7 }}
        >
          {stepNum}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <span className="text-[13px] font-semibold leading-tight text-foreground">{step.label}</span>
        {step.sub && <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">{step.sub}</span>}
        {step.content && <div className="mt-2.5">{step.content}</div>}
      </div>
    </div>
  );
}

function FlowColumn({
  steps,
  color,
  title,
  numbered = true,
}: {
  steps: FlowStep[];
  color: string;
  title: string;
  numbered?: boolean;
}) {
  return (
    <div
      className="flex min-w-0 flex-col gap-0 border p-2.5"
      style={{ borderColor: `${color}30`, backgroundColor: `${color}04`, borderRadius: 8 }}
    >
      <div className="mb-2 border-b pb-2" style={{ borderColor: `${color}25` }}>
        <span className="text-sm font-bold" style={{ color }}>
          {title}
        </span>
      </div>

      {steps.map((step, i) => (
        <div key={step.label} className="flex w-full flex-col items-center">
          <StepBox step={step} color={color} stepNum={numbered ? i + 1 : undefined} />
          {i < steps.length - 1 && <StepArrow color={color} />}
        </div>
      ))}
    </div>
  );
}

// ── Step 2: risk spectrum with product badges ──────────────────

const RISK_PRODUCTS = [
  { key: "savings" as const, pos: 0.04, row: 0 as const },
  { key: "timeDeposit" as const, pos: 0.15, row: 1 as const },
  { key: "govBond" as const, pos: 0.26, row: 0 as const },
  { key: "corpBond" as const, pos: 0.38, row: 1 as const },
  { key: "fund" as const, pos: 0.5, row: 0 as const },
  { key: "etf" as const, pos: 0.61, row: 1 as const },
  { key: "reit" as const, pos: 0.72, row: 0 as const },
  { key: "equity" as const, pos: 0.83, row: 1 as const },
  { key: "crypto" as const, pos: 0.95, row: 0 as const },
];

function RiskSpectrumLine() {
  const t = STRINGS[useDocLocale()];
  return (
    <div>
      {/* Upper row badges */}
      <div className="relative mb-1 h-5">
        {RISK_PRODUCTS.filter((p) => p.row === 0).map((p) => (
          <span
            key={p.key}
            className="absolute bottom-0 -translate-x-1/2 whitespace-nowrap text-[10px] font-medium text-foreground"
            style={{ left: `${p.pos * 100}%` }}
          >
            {t.product[p.key]}
          </span>
        ))}
      </div>

      {/* Gradient bar with tick marks */}
      <div className="relative h-2.5">
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: `linear-gradient(to right, ${COLOR.emerald}, ${COLOR.amber}, ${COLOR.rose})`,
            opacity: 0.55,
          }}
        />
        {RISK_PRODUCTS.map((p) => (
          <div
            key={p.key}
            className="absolute top-0 h-full w-px bg-background/70"
            style={{ left: `${p.pos * 100}%` }}
          />
        ))}
      </div>

      {/* Lower row badges */}
      <div className="relative mt-1 h-5">
        {RISK_PRODUCTS.filter((p) => p.row === 1).map((p) => (
          <span
            key={p.key}
            className="absolute top-0 -translate-x-1/2 whitespace-nowrap text-[10px] font-medium text-foreground"
            style={{ left: `${p.pos * 100}%` }}
          >
            {t.product[p.key]}
          </span>
        ))}
      </div>

      <div className="mt-0.5 flex justify-between text-[10px] text-muted-foreground">
        <span>{t.riskLow}</span>
        <span>{t.riskHigh}</span>
      </div>
    </div>
  );
}

// ── Step 3: allocation slider with risk/return gauges ────────────

const ASSET_CLASSES = [
  { key: "deposit" as const, color: COLOR.amber, expectedReturn: 0.1, risk: 0 },
  { key: "bond" as const, color: COLOR.sky, expectedReturn: 3.0, risk: 5 },
  { key: "equity" as const, color: COLOR.emerald, expectedReturn: 20.0, risk: 30 },
] as const;

function AllocationSlider() {
  const t = STRINGS[useDocLocale()];
  const [growthRatio, setGrowthRatio] = useState(50);

  const cashPct = 15;
  const remaining = 100 - cashPct;
  const stockPct = Math.round((remaining * growthRatio) / 100);
  const bondPct = remaining - stockPct;

  const wReturn =
    (cashPct / 100) * ASSET_CLASSES[0].expectedReturn +
    (bondPct / 100) * ASSET_CLASSES[1].expectedReturn +
    (stockPct / 100) * ASSET_CLASSES[2].expectedReturn;
  const wRisk = Math.sqrt(
    (bondPct / 100) ** 2 * ASSET_CLASSES[1].risk ** 2 + (stockPct / 100) ** 2 * ASSET_CLASSES[2].risk ** 2
  );

  const allocations = [
    { ...ASSET_CLASSES[0], pct: cashPct },
    { ...ASSET_CLASSES[1], pct: bondPct },
    { ...ASSET_CLASSES[2], pct: stockPct },
  ];

  return (
    <div className="space-y-2">
      {/* Slider */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
          <span>{t.defensive}</span>
          <span>{t.growth}</span>
        </div>
        <Slider value={[growthRatio]} onValueChange={([v]) => setGrowthRatio(v)} min={0} max={100} step={1} />
      </div>

      {/* Allocation bar */}
      <div className="flex h-6 w-full overflow-hidden rounded-md border border-border/40">
        {allocations.map((a) => (
          <div
            key={a.key}
            className="flex items-center justify-center overflow-hidden text-[9px] font-bold text-white transition-all duration-200"
            style={{ backgroundColor: a.color, opacity: 0.72, width: `${a.pct}%` }}
          >
            {a.pct >= 14 ? `${t.product[a.key]} ${a.pct}%` : ""}
          </div>
        ))}
      </div>

      {/* Gauges — inline */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">{t.expectedReturn}</span>
          <span className="font-semibold tabular-nums text-emerald-600">{wReturn.toFixed(1)}%</span>
          <span className="text-muted-foreground/70">
            （+{t.money(Math.round((1000000 * wReturn) / 100))}）
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground">{t.risk}</span>
          <span className="font-semibold tabular-nums text-amber-600">±{wRisk.toFixed(1)}%</span>
          <span className="text-muted-foreground/70">
            （±{t.money(Math.round((1000000 * wRisk) / 100))}）
          </span>
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground/60">{t.footnote}</p>
    </div>
  );
}

// ── Export ────────────────────────────────────────────────────────

export function DocDecisionFlowDiagram() {
  const t = STRINGS[useDocLocale()];
  const recommendedSteps: FlowStep[] = [
    { label: t.recommended.roles },
    { label: t.recommended.products, content: <RiskSpectrumLine /> },
    { label: t.recommended.weights, content: <AllocationSlider /> },
    { label: t.recommended.sustain },
  ];

  return (
    <IllustrationCard title={t.flowTitle} description={t.flowDescription}>
      <div className="grid gap-4 md:grid-cols-3">
        <FlowColumn steps={t.common} color={COLOR.amber} title={t.commonColumn} />
        <div className="md:col-span-2">
          <FlowColumn steps={recommendedSteps} color={COLOR.emerald} title={t.recommendedColumn} />
        </div>
      </div>
    </IllustrationCard>
  );
}
